import type { KnownEid } from '@common/electronic-id/eidProviders';
import { OrganizationSector } from './organizations/organizationSector';

interface Config {
  digipostIdpUrl: string;
  isDevModeEnabled?: boolean;
  enableScroll?: boolean;
  enableExternalNavigation?: boolean;
  registerOrganizationUrl?: string;
  sentry?: {
    enabled: boolean;
    environment?: string;
    dsn?: string;
    maxCharCount?: number;
  };
  session?: {
    idleTimeout: number;
    sessionTimeout: number;
    refreshInterval: number;
  };
  organizationLoginProviders?: KnownEid[];
  privateLoginProviders?: KnownEid[];
  newOrgSigningAvailableFor: OrganizationSector[];
}

interface ResolvedConfig {
  digipostIdpUrl: string;
  isDevModeEnabled: boolean;
  enableScroll: boolean;
  enableExternalNavigation: boolean;
  registerOrganizationUrl: string;
  sentry: {
    enabled: boolean;
    environment?: string;
    dsn?: string;
    maxCharCount?: number;
  };
  session: {
    idleTimeout: number;
    sessionTimeout: number;
    refreshInterval: number;
  };
  organizationLoginProviders: KnownEid[];
  privateLoginProviders: KnownEid[];
  newOrgSigningAvailableFor: OrganizationSector[];
}

const localConfig: Config = {
  digipostIdpUrl: 'http://localhost:9003',
  isDevModeEnabled: true,
  enableScroll: true,
  enableExternalNavigation: true,
  registerOrganizationUrl: 'https://localhost:7171/api/begin?context=signering',
  organizationLoginProviders: ['bankid', 'buypass', 'entra'],
  newOrgSigningAvailableFor: [
    OrganizationSector.PRIVATE,
    OrganizationSector.PUBLIC,
  ],
};

const config: Config =
  (window as { signeringConfig?: Config }).signeringConfig || localConfig;

function valueOrDefault<T>(defaultValue: T, value?: T): T {
  return value === undefined ? defaultValue : value;
}

export const resolvedConfig: ResolvedConfig = {
  digipostIdpUrl: valueOrDefault(
    'https://login.digipost.no',
    config.digipostIdpUrl
  ),
  isDevModeEnabled: valueOrDefault(false, config.isDevModeEnabled),
  enableScroll: valueOrDefault(true, config.enableScroll),
  enableExternalNavigation: valueOrDefault(
    true,
    config.enableExternalNavigation
  ),
  registerOrganizationUrl: valueOrDefault(
    'https://www.digipost.no/registrering/bedrift/login?redirect-url-signering=https%3A%2F%2Flocalhost%3A9000%2Fvirksomhet%2Flogginn%2Fprivat%3Fsource%3Dfullf%C3%B8rt-registrering',
    config.registerOrganizationUrl
  ),
  sentry: valueOrDefault({ enabled: false }, config.sentry),
  session: valueOrDefault(
    {
      idleTimeout: 1000 * 60,
      sessionTimeout: 1000 * 60 * 30,
      refreshInterval: 1000 * 60 * 5,
    },
    config.session
  ),
  organizationLoginProviders: valueOrDefault(
    ['bankid', 'buypass'],
    config.organizationLoginProviders
  ),
  privateLoginProviders: valueOrDefault(
    ['bankid', 'buypass'],
    config.privateLoginProviders
  ),
  newOrgSigningAvailableFor: valueOrDefault(
    [],
    config.newOrgSigningAvailableFor
  ),
};

// Overstyr verdier for kjøring av tester (altså ikke miljøet test.signering.posten.no)
if (process.env.NODE_ENV === 'test') {
  resolvedConfig.enableScroll = false;
  resolvedConfig.enableExternalNavigation = false;
}

export default resolvedConfig;
